import React, { FC, useState, KeyboardEvent } from 'react';

import Link from 'next/link';
import { useRouter } from 'next/router';

import { useRecoilState, useResetRecoilState } from 'recoil';

import Iqloud_PasswordInput from '@components_iqloud/form/Iqloud_PasswordInput';
import NotificationBar from '@components_iqloud/form/NotificationBar';
import RefTextInput from '@components_iqloud/form/RefTextInput';

import {
  lastSearcheMansionIdAtom,
  currentMansionDisplayNameAtom,
  prefectureAtom,
  cityAtom,
  townAtom,
  propertyTypeAtom,
  lastSearchedTownQueryAtom,
  hasInitializedAtom,
  neighboringSellingContractDataAtom,
  searchResultDataAtom,
} from '@recoil_iqfu/atoms/iqfu_pro/searchQuery';

import { consoleUserType } from '@constants_common';
import { NotificationText } from '@constants_iqfu';
import { useAuth } from '@contexts_common/authContext';

import styles from './Login.module.scss';

type Props = {
  userType: consoleUserType;
};

const Login: FC<Props> = ({ userType }) => {
  const { dashboardLogin } = useAuth();
  const router = useRouter();
  const [showError, setShowError] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const [data, setData] = useState({
    email: '',
    password: '',
  });

  //イクラ不動産プロの検索状態をログイン時にリセットするための関数
  const [iqfuPropropertyType, setIqfuProPropertyType] = useRecoilState(propertyTypeAtom);
  const resetIqfuProLastSearchMansionId = useResetRecoilState(lastSearcheMansionIdAtom);
  const resetIqfuProCurrentMansionDisplayName = useResetRecoilState(currentMansionDisplayNameAtom);
  const resetIqfuProPrefecture = useResetRecoilState(prefectureAtom);
  const resetIqfuProCity = useResetRecoilState(cityAtom);
  const resetIqfuProTown = useResetRecoilState(townAtom);
  const resetIqfuProLastSearchedTownQuery = useResetRecoilState(lastSearchedTownQueryAtom);
  const resetIqfuProHasInitialized = useResetRecoilState(hasInitializedAtom);
  const resetIqfuProSearchResult = useResetRecoilState(searchResultDataAtom);
  const resetIqfuProNeighboringSellingContract = useResetRecoilState(neighboringSellingContractDataAtom);

  const resetIqfuProState = () => {
    setIqfuProPropertyType('landhouse');
    resetIqfuProLastSearchMansionId();
    resetIqfuProCurrentMansionDisplayName();
    resetIqfuProPrefecture();
    resetIqfuProCity();
    resetIqfuProTown();
    resetIqfuProLastSearchedTownQuery();
    resetIqfuProHasInitialized();
    resetIqfuProSearchResult();
    resetIqfuProNeighboringSellingContract();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setShowError(false);
    const target = e.target;
    const name = target.name;
    const value = target.value;
    setData(() => {
      return { ...data, [name]: value };
    });
  };

  async function onSubmitHandler() {
    setLoading(true);
    // stateからemailとpasswordを取得する
    const { email, password } = data;

    const formData = new FormData();
    formData.append('email', email);
    formData.append('password', password);
    (async () => {
      const { data, success } = await dashboardLogin(formData, userType);

      if (success) {
        //イクラ不動産プロユーザーの不動産会社には、検索ボックスの初期値として会社情報の住所をセットする
        if (userType === 'company' && data?.services?.includes('pro')) {
          resetIqfuProState();
        }

        const redirectUrl = router.query.redirect ? decodeURIComponent(router.query.redirect as string) : undefined;

        if (redirectUrl) {
          //redirectUrlがquery parameterをもってるかで分岐
          if (redirectUrl.includes('?')) {
            router.push(redirectUrl + '&hasRedirected=true');
          } else {
            router.push(redirectUrl + '?hasRedirected=true');
          }
        } else {
          if (userType === 'financial_agency') {
            router.push(`/console/financial_agency/chat/contact`);
          } else {
            router.push(`/console/${userType}/dashboard`);
          }
        }
      } else {
        setLoading(false);
        setShowError(true);
      }
    })();
  }

  return (
    <div className={`${styles.login_container} ${userType === 'admin' && styles.admin}`}>
      <NotificationBar
        open={showError}
        toggle={(prevState: boolean) => setShowError(!prevState)}
        close={() => setShowError(false)}
        message={NotificationText.loginFailed}
        status={'alert'}
      />

      <div className={styles.login_card}>
        {userType === 'admin' && <div className={styles.login_card_title_admin}>管理者用</div>}
        <img src={`/images/iqfu/login_logo.svg`} className={styles.service_logo} alt="イクラ不動産ロゴ" />
        <div className={styles.form}>
          <RefTextInput
            type="text"
            name="email"
            placeholder="メールアドレス"
            className={styles.input}
            onChange={handleInputChange}
            onKeyPress={(e: KeyboardEvent<HTMLInputElement>) => {
              if (e.key == 'Enter') onSubmitHandler();
            }}
          />
          <Iqloud_PasswordInput
            name="password"
            placeholder="パスワード"
            className={styles.input}
            onChange={handleInputChange}
            onKeyPress={(e: KeyboardEvent<HTMLInputElement>) => {
              if (e.key == 'Enter') onSubmitHandler();
            }}
          />
          {/* <div className={styles.checkbox_row}>
            <input type="checkbox" className={styles.checkbox} />
            ログイン状態を保持する
          </div> */}
          <button
            className={`${styles.login_button} ${userType === 'admin' && styles.admin}`}
            onClick={() => onSubmitHandler()}
          >
            ログイン
          </button>
          {userType === 'company' && (
            <Link href="/console/company/forgot-password">
              <a className={styles.forget_pass}>パスワードをお忘れの方</a>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
